import { useState } from "react";

import { Badge } from "@/components/badge";
import { type Column, DataTable } from "@/components/data-table";
import { Field, Select } from "@/components/field";
import { StatusMessage } from "@/components/status-message";
import { useOrgSelection } from "@/context/use-org-selection";
import {
	type SensorSpecWorkspace,
	useSensorSpecEditor,
	useSensorSpecWorkspace,
} from "@/features/plausibility/use-sensor-specs";

import { DraftBar } from "./DraftBar";
import { SpecEditor } from "./SpecEditor";
import { changedFields, specFields } from "./taxonomy-view";

interface OverrideRow {
	active: boolean;
	classId: string;
	featureCount: number;
	sensorId: string;
	sensorName: string;
	state: "changed" | "live" | "new";
}

/**
 * Per-camera feature specs for the selected organization. An override
 * replaces the global spec wholesale for one camera, usually one whose optics
 * make a global feature unreliable (a fisheye lens and a size-against-door
 * cue). Resist scoping early: every override splits the calibration data.
 * Staged in the organization's own draft; published separately from the
 * global taxonomy.
 */
export const SensorSpecs = () => {
	const { selected } = useOrgSelection();
	const workspace = useSensorSpecWorkspace();
	const editor = useSensorSpecEditor();
	const [sensorId, setSensorId] = useState("");
	const [classId, setClassId] = useState("");
	const [published, setPublished] = useState<string>();
	const data = workspace.data;
	const busy =
		editor.saveSpec.isPending ||
		editor.revertSpec.isPending ||
		editor.publish.isPending ||
		editor.discard.isPending;

	const rows = data ? overrideRows(data) : [];
	const sensorName = (id: string) =>
		data?.sensors.find((sensor) => sensor.id === id)?.name ?? id;
	const alertable = (data?.classes ?? []).filter((cls) => cls.alertable);
	const classOptions = (data?.classes ?? [])
		.filter((cls) => cls.classId !== classId)
		.map((cls) => ({
			label: `${cls.classId} — ${cls.displayName}`,
			value: cls.classId,
		}));

	const editing =
		data && sensorId && classId ? current(data, sensorId, classId) : null;

	const columns: Column<OverrideRow>[] = [
		{ header: "Camera", key: "sensor", render: (row) => row.sensorName },
		{
			header: "Class",
			key: "class",
			render: (row) => <code className="text-xs">{row.classId}</code>,
		},
		{
			header: "Features",
			key: "features",
			render: (row) => row.featureCount,
		},
		{
			header: "Status",
			key: "status",
			render: (row) => (
				<div className="flex gap-1">
					{!row.active && <Badge label="inactive" value="skipped" />}
					{row.state !== "live" && (
						<Badge label={`${row.state} in draft`} value="pending" />
					)}
				</div>
			),
		},
	];

	const review = (data?.draft?.specs ?? []).map((change) => ({
		key: `${change.scopeRef}:${change.classId}`,
		label: `${sensorName(change.scopeRef ?? "")} · ${change.classId}: ${changedFields(change.base, change.value).join(", ")}`,
		onRevert: () =>
			editor.revertSpec.mutate({
				classId: change.classId,
				sensorId: change.scopeRef ?? "",
			}),
	}));

	return (
		<section className="flex flex-col gap-4">
			<div>
				<h2 className="text-primary text-base font-semibold">
					Camera feature overrides{selected ? ` — ${selected.name}` : ""}
				</h2>
				<p className="text-secondary text-sm">
					Replace a class's global features for one camera. Tenant database;
					published separately from the global taxonomy.
				</p>
			</div>

			<StatusMessage
				error={workspace.error}
				isLoading={workspace.isLoading}
				loading="Loading cameras…"
			/>

			{data && (
				<DraftBar
					busy={busy}
					confirmMessage="Publish these camera overrides? The next verification on each camera uses them."
					draft={data.draft}
					error={editor.publish.error ?? editor.discard.error}
					issues={data.issues}
					onDiscard={() => editor.discard.mutate()}
					onPublish={() =>
						editor.publish.mutate(undefined, {
							onSuccess: (result) =>
								setPublished(`Published ${result.specs} camera override(s).`),
						})
					}
					published={published}
					review={review}
				/>
			)}

			{data && (
				<div className="flex flex-wrap items-end gap-3">
					<Field htmlFor="override-sensor" label="Camera">
						<Select
							id="override-sensor"
							onChange={(event) => setSensorId(event.target.value)}
							value={sensorId}
						>
							<option value="">Choose…</option>
							{data.sensors.map((sensor) => (
								<option key={sensor.id} value={sensor.id}>
									{sensor.name}
								</option>
							))}
						</Select>
					</Field>
					<Field htmlFor="override-class" label="Class">
						<Select
							id="override-class"
							onChange={(event) => setClassId(event.target.value)}
							value={classId}
						>
							<option value="">Choose…</option>
							{alertable.map((cls) => (
								<option key={cls.classId} value={cls.classId}>
									{cls.classId}
								</option>
							))}
						</Select>
					</Field>
				</div>
			)}

			{editing && (
				<section className="border-secondary rounded-md border p-4">
					<p className="text-secondary mb-3 text-sm">
						{editing.source === "override"
							? "This camera has its own features for this class."
							: editing.source === "draft"
								? "Staged override for this camera."
								: "No override: this camera uses the global features below. Saving creates an override starting from them."}
					</p>
					<SpecEditor
						busy={busy}
						classOptions={classOptions}
						error={editor.saveSpec.error ?? editor.revertSpec.error}
						key={`${sensorId}:${classId}:${JSON.stringify(editing.value)}`}
						lockedKeys={editing.lockedKeys}
						onRevert={() => editor.revertSpec.mutate({ classId, sensorId })}
						onSave={(fields) =>
							editor.saveSpec.mutate({ classId, fields, sensorId })
						}
						prefix={`override-${sensorId}-${classId}`}
						staged={editing.source === "draft"}
						value={editing.value}
					/>
				</section>
			)}

			{data && (
				<>
					<StatusMessage
						empty="No camera has its own features. Every camera uses the global taxonomy."
						isEmpty={rows.length === 0}
					/>
					{rows.length > 0 && (
						<DataTable
							columns={columns}
							onRowClick={(row) => {
								setSensorId(row.sensorId);
								setClassId(row.classId);
							}}
							rowKey={(row) => `${row.sensorId}:${row.classId}`}
							rows={rows}
						/>
					)}
				</>
			)}
		</section>
	);
};

function current(data: SensorSpecWorkspace, sensorId: string, classId: string) {
	const change = data.draft?.specs.find(
		(c) => c.scopeRef === sensorId && c.classId === classId,
	);
	const override = data.overrides.find(
		(o) => o.scopeRef === sensorId && o.classId === classId,
	);
	const global = data.globalSpecs.find((g) => g.classId === classId)?.fields;
	const lockedKeys =
		(override ? specFields(override) : global)?.features.map((f) => f.key) ??
		[];
	if (change)
		return { lockedKeys, source: "draft" as const, value: change.value };
	if (override) {
		return {
			lockedKeys,
			source: "override" as const,
			value: specFields(override),
		};
	}
	return {
		lockedKeys,
		source: "global" as const,
		value: global ?? { active: true, confusers: [], features: [] },
	};
}

/** Live overrides and staged ones, with the draft's value where it has one. */
function overrideRows(data: SensorSpecWorkspace): OverrideRow[] {
	const name = (id: string) =>
		data.sensors.find((sensor) => sensor.id === id)?.name ?? id;
	const staged = new Map(
		(data.draft?.specs ?? []).map((change) => [
			`${change.scopeRef}:${change.classId}`,
			change,
		]),
	);
	const rows = new Map<string, OverrideRow>();
	for (const override of data.overrides) {
		const key = `${override.scopeRef}:${override.classId}`;
		const change = staged.get(key);
		const fields = change ? change.value : specFields(override);
		rows.set(key, {
			active: fields.active,
			classId: override.classId,
			featureCount: fields.features.length,
			sensorId: override.scopeRef ?? "",
			sensorName: name(override.scopeRef ?? ""),
			state: change ? "changed" : "live",
		});
	}
	for (const [key, change] of staged) {
		if (rows.has(key)) continue;
		rows.set(key, {
			active: change.value.active,
			classId: change.classId,
			featureCount: change.value.features.length,
			sensorId: change.scopeRef ?? "",
			sensorName: name(change.scopeRef ?? ""),
			state: "new",
		});
	}
	return [...rows.values()].sort(
		(a, b) =>
			a.sensorName.localeCompare(b.sensorName) ||
			a.classId.localeCompare(b.classId),
	);
}
